import { useState } from 'react';
import { createFileRoute, Link } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { Leaf } from 'lucide-react';
import {
  CARBON_PLANNING_CROSS_LINK_TO_ENVIRONMENTAL,
  ESG_CAT1_OR_2_HELPER,
  ESG_UNLOCATED_ASSETS_NOTE,
  SUSTAINABILITY_TRAJECTORY_DEFAULT_HORIZON,
  SUSTAINABILITY_TRAJECTORY_HORIZON_OPTIONS,
  orgTargetChartLabel,
} from '@siliconx/shared';
import { Card } from '@siliconx/shared/ui/Card';
import { PageHeader } from '@siliconx/shared/ui/PageHeader';
import { SectionLabel } from '@siliconx/shared/ui/SectionLabel';
import { LoadingShell, ErrorState } from '@siliconx/shared/ui/state';
import { api, throwIfNotOk } from '../../lib/api';
import { qk } from '../../lib/queryKeys';
import {
  CarbonFramingLine,
  ConstantGridIntensityFootnote,
  LocationBasedMethodBadge,
  LocationBasedMethodNote,
} from '../../components/financials/sustainability/CarbonDisclosures';
import { DataCoverageStrip } from '../../components/financials/sustainability/DataCoverageStrip';
import { EsgExportCard } from '../../components/financials/sustainability/EsgExportCard';
import { SustainabilityTrajectoryChart } from '../../components/financials/sustainability/SustainabilityTrajectoryChart';
import { FyReductionTargetCard } from '../../components/financials/carbon/FyReductionTargetCard';
import { RegulatoryRegisterLink } from '../../components/environmental/RegulatoryRegisterLink';
import { requireOrgFeature } from '../../lib/orgFeatureGate';

/**
 * SIL-442 (W4) — Carbon Planning (nav label per SIL-486; nee
 * "Sustainability"): the baseline trajectory dashboard. Spec §6 (IA
 * placement: "a Sustainability entry inside the Financials pillar, beside
 * Scenarios"), §8-9, copy locked in
 * resources/plans/sil-426-carbon-disclosure-copy.md.
 *
 * URL stays under `/financials/*` — the same deliberate presentation-vs-URL
 * split `/financials/scenarios` already carries (see CUSTOMER_NAV's SIL-307
 * note in shell/AppShell.tsx): the nav entry sits beside Scenarios, the
 * route lives with its siblings. SIL-486 renamed the nav label only — the
 * route stayed `/financials/sustainability` so no bookmark/deep link 404s.
 *
 * FOUR THINGS, NO MORE:
 *   1. hold-everything baseline trajectory vs the org target line;
 *   2. the §9 data-coverage strip — CAVEATED, NEVER GATED;
 *   3. the standard disclosures (§1 framing line, §2 method badge + note,
 *      §3 constant-intensity footnote);
 *   4. the three-file ESG export, wired to the W3 (SIL-438) endpoints.
 *
 * This surface holds NO advisory (avoided-emissions) figures. Those live
 * exclusively in the scenario surfaces' firewalled advisory module and must
 * never appear on, or share an axis with, this footprint trajectory.
 *
 * SIL-1108: `orgFeature: 'sustainability'` (customerNav.ts) is
 * business-only. Gated in this leaf route's own `beforeLoad` (in addition
 * to the parent `/financials` layout's `'finance'` gate) so this screen
 * stays covered even if a future org type unlocks one feature without the
 * other.
 */
export const Route = createFileRoute('/financials/sustainability')({
  beforeLoad: () => requireOrgFeature('sustainability'),
  component: SustainabilityPage,
});

function SustainabilityPage() {
  const [horizonMonths, setHorizonMonths] = useState<number>(SUSTAINABILITY_TRAJECTORY_DEFAULT_HORIZON);

  const trajectoryQ = useQuery({
    queryKey: qk.sustainability.trajectory(horizonMonths),
    queryFn: async () => {
      const res = await api.api.client.sustainability.trajectory.$get({
        query: { horizonMonths: String(horizonMonths) },
      });
      await throwIfNotOk(res);
      return res.json();
    },
  });

  return (
    <div className="space-y-5">
      <PageHeader
        title="Carbon Planning"
        pinTo="/financials/sustainability"
        subtitle="Where your fleet's modeled emissions go if you change nothing — and how that compares to your target."
        actions={
          <label className="flex items-center gap-2 text-xs text-zinc-600">
            Horizon
            <select
              value={horizonMonths}
              onChange={(e) => setHorizonMonths(Number(e.target.value))}
              className="rounded-md border border-zinc-300 bg-white px-2 py-1.5 text-xs text-zinc-800"
            >
              {SUSTAINABILITY_TRAJECTORY_HORIZON_OPTIONS.map((months) => (
                <option key={months} value={months}>
                  {months} months
                </option>
              ))}
            </select>
          </label>
        }
      />

      {/* SIL-486: one-line cross-link to the backward-looking counterpart
       * dashboard (sustainability-dashboard-spec.md §1). */}
      <Link to="/environmental" className="block text-xs text-brand-700 hover:underline" data-testid="cross-link-environmental">
        {CARBON_PLANNING_CROSS_LINK_TO_ENVIRONMENTAL}
      </Link>

      {/* SIL-611: Regulatory-register cross-link at the TOP of the page,
       * beside the Environmental cross-link, so it is visible without
       * scrolling. It previously sat mid-page inside the forecast card's
       * disclosure stack. */}
      <RegulatoryRegisterLink />

      {trajectoryQ.isLoading && <LoadingShell rows={6} />}
      {trajectoryQ.isError && (
        <ErrorState
          title="Could not load the sustainability trajectory"
          error={trajectoryQ.error}
          onRetry={() => void trajectoryQ.refetch()}
        />
      )}

      {trajectoryQ.data && (
        <>
          <DataCoverageStrip coverage={trajectoryQ.data.coverage} />

          {/* Row 1 — the FORECAST card. Same violet dashed treatment as
           * ScenarioCarbonForecast (SIL-349): a different color family, a
           * dashed border, and the word "forecast" in a badge, so a
           * screenshot still carries the forecast-vs-inventory distinction
           * without relying on color. */}
          <div className="rounded-xl border-2 border-dashed border-violet-300 bg-violet-50/60 p-5">
            <div className="flex flex-wrap items-center gap-2">
              <span className="inline-flex items-center rounded bg-violet-600 px-1.5 py-0.5 text-xs font-bold uppercase tracking-wider text-white">
                Forecast
              </span>
              <SectionLabel className="text-violet-900">
                Baseline trajectory — hold everything
              </SectionLabel>
              <LocationBasedMethodBadge />
            </div>

            <CarbonFramingLine className="mt-2" />

            <p className="mt-1.5 max-w-3xl text-xs text-violet-800">
              Cumulative modeled emissions over {trajectoryQ.data.horizonMonths} months if every asset is held
              as-is: no sales, no purchases, no disposals, no moves. PUE{' '}
              {trajectoryQ.data.pue.value.toFixed(2)}{' '}
              applied —{' '}
              {trajectoryQ.data.pue.source === 'manual_entry'
                ? 'your own value'
                : 'published industry default'}
              . Whole-fleet rate{' '}
              {Math.round(trajectoryQ.data.fleetAnnualRateKgco2ePerYr).toLocaleString('en-US')} kg CO2e/yr.
            </p>

            <div className="mt-4 rounded-lg border border-violet-200 bg-white p-3">
              <SustainabilityTrajectoryChart
                series={trajectoryQ.data.series}
                targetTco2ePerYr={trajectoryQ.data.orgTarget?.tco2ePerYr ?? null}
              />
            </div>

            <div className="mt-3 space-y-2 border-t border-violet-200 pt-3">
              {trajectoryQ.data.orgTarget?.tco2ePerYr != null ? (
                <p className="text-xs text-violet-900">
                  <span className="font-medium">
                    {orgTargetChartLabel(trajectoryQ.data.orgTarget.tco2ePerYr)}
                  </span>{' '}
                  — an absolute annual budget, accrued month by month so it can be read against the cumulative
                  line above.
                </p>
              ) : trajectoryQ.data.orgTarget == null ? (
                <p className="text-xs text-violet-900">
                  No organization target set. Set one in Financials setup to draw a target line here.
                </p>
              ) : (
                // SIL-474: a reduction_percent org target has no flat
                // annual number to draw against the cumulative line above
                // — the FY target card below is its natural home instead
                // (see FyReductionTargetCard.tsx's header).
                <p className="text-xs text-violet-900">
                  Organization target: {trajectoryQ.data.orgTarget.reductionPct}% below{' '}
                  {trajectoryQ.data.orgTarget.baseYear} by {trajectoryQ.data.orgTarget.targetYear} — a reduction
                  target, not a flat annual budget, so no line is drawn on the chart above. See the FY target
                  card below.
                </p>
              )}
              <ConstantGridIntensityFootnote />
              <LocationBasedMethodNote />
              <p className="text-xs leading-relaxed text-violet-800">{ESG_UNLOCATED_ASSETS_NOTE}</p>
              <p className="text-xs leading-relaxed text-violet-800">{ESG_CAT1_OR_2_HELPER}</p>
            </div>

            {trajectoryQ.data.orgTarget?.targetType === 'reduction_percent' && (
              <div className="mt-3">
                <FyReductionTargetCard
                  target={trajectoryQ.data.orgTarget}
                  currentAnnualTco2e={trajectoryQ.data.fleetAnnualRateKgco2ePerYr / 1000}
                />
              </div>
            )}
          </div>

          <EsgExportCard />

          {/* Where the numbers on this page do NOT come from — stated
           * plainly rather than left to inference. */}
          <Card padding="md">
            <SectionLabel className="inline-flex items-center gap-1.5">
              <Leaf className="size-3.5 text-zinc-400" />
              What this page is not
            </SectionLabel>
            <p className="mt-1.5 max-w-3xl text-xs leading-relaxed text-zinc-500">
              This is the do-nothing baseline. What-if actions (sell, buy, move, refresh) and their avoided-
              emissions advisory figures live in Scenarios; advisory figures are never summed with, netted
              against, or drawn on the same axis as the footprint shown here.
            </p>
          </Card>
        </>
      )}
    </div>
  );
}
